// Drag-and-drop for the documents page: the sensors and collision strategy
// for the DndContext, the drag-end rules (what may move where), and the brief
// "invalid drop" flash a refused row plays. The page renders the DndContext;
// this hook decides everything about it.

import { useEffect, useRef, useState } from 'react';
import {
  PointerSensor, closestCenter, pointerWithin, useSensor, useSensors,
  type CollisionDetection, type DragEndEvent,
} from '@dnd-kit/core';
import type { IFolder } from '@apis/documents';
import { stripTableRowDndId } from '../DocumentView/DocumentTable/tableRowDndId';

const INVALID_DROP_FLASH_MS = 450; // How long the invalid-drop flash stays on a row before it clears.

interface IUseDocumentsDndArgs {
  folders: IFolder[];
  moveFolder: (id: string, toId: string) => void;
  moveDoc: (id: string, toFolderId: string) => void;
}

export function useDocumentsDnd({ folders, moveFolder, moveDoc }: IUseDocumentsDndArgs) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const collisionDetection: CollisionDetection = (args) => {
    if (args.active.data.current?.type === 'document') return pointerWithin(args);
    return closestCenter(args);
  };

  // The id of an item whose drop was just refused, held briefly so the row can play a rejection flash.
  const [invalidDropId, setInvalidDropId] = useState<string | null>(null);
  const invalidDropTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (invalidDropTimer.current) clearTimeout(invalidDropTimer.current); }, []);

  function flashInvalidDrop(id: string) {
    setInvalidDropId(id);
    invalidDropTimer.current = setTimeout(() => setInvalidDropId(null), INVALID_DROP_FLASH_MS);
  }

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over) return;
    const type = active.data.current?.type as 'folder' | 'document' | undefined;
    // Table folder rows register under a namespaced dnd id (the bare id is
    // taken by the sidebar node for the same folder) — normalise both sides
    // so the move/cycle logic runs on real folder ids. This also catches
    // dragging a folder's table row onto its own sidebar node (and vice versa).
    const activeId = stripTableRowDndId(String(active.id));
    const overId = stripTableRowDndId(String(over.id));
    if (activeId === overId) return;
    if (type === 'folder') {
      // Dropping onto the virtual root would make a root-level sibling of the
      // server-owned roots — not allowed.
      if (overId === 'all') { flashInvalidDrop(activeId); return; }
      // The roots are separate stores upstream — no cross-root moves.
      if (activeId.split(':')[0] !== overId.split(':')[0]) { flashInvalidDrop(activeId); return; }
      let currentFolderId: string | null = overId;
      while (currentFolderId !== null) {
        if (currentFolderId === activeId) { flashInvalidDrop(activeId); return; }
        currentFolderId = folders.find((f) => f.id === currentFolderId)?.parentId ?? null;
      }
      moveFolder(activeId, overId);
    } else if (type === 'document') {
      if (overId === 'all') { flashInvalidDrop(activeId); return; }
      // The roots are separate stores upstream — no cross-root moves.
      if (activeId.split(':')[0] !== overId.split(':')[0]) { flashInvalidDrop(activeId); return; }
      moveDoc(activeId, overId);
    }
  }

  return { sensors, collisionDetection, invalidDropId, handleDragEnd };
}
